<script setup>

import {onMounted, ref, provide, computed} from "vue";

const props = defineProps({
    leadId: {
        type: Number,
        required: true,
    },
    lead: Object,
    events: Object,
    actions: Object,
    deals: Object,
    done_actions: Object,
    stakeholders: Object,
    internal: Number,
    invite: String,
    signatureRequests: Object,
    legacyDeals: String,
    smsDeliveries: Object,
    emailDeliveries: Object,
    processes: Object,
})

const currentTab = ref('')
const tabs = ref([
    {
        label: _mft('crm:leadPage.tabTitle.showAll'),
        name: 'all'
    },
    {
        label: _mft('crm:leadPage.tabTitle.details'),
        name: 'details'
    },
    {
        label: _mft('crm:leadPage.tabTitle.contracts'),
        name: 'signatures'
    },
    {
        label: _mft('crm:leadPage.tabTitle.automations'),
        name: 'deliveries'
    },
    {
        label: _mft('crm:leadPage.tabTitle.notes'),
        name: 'notes'
    },
    {
        label: _mft('crm:leadPage.tabTitle.actions'),
        name: 'actions'
    },
    {
        label: _mft('crm:leadPage.tabTitle.stakeholders'),
        name: 'stakeholders'
    },
])

const lead = ref(null)
const showInsights = ref(false)

const GENERIC_EMAIL_DOMAINS = [
    'gmail.com',
    'hotmail.com',
    'yahoo.com',
    'outlook.com',
    'live.com',
    'aol.com',
    'icloud.com',
    'me.com',
    'msn.com',
    'mail.com',
    'inbox.com',
    'gmx.com',
    'zoho.com',
    'yandex.com',
    'protonmail.com',
    'tutanota.com',
    'fastmail.com',
    'hushmail.com',
    'telia.com',
    'bredband.net',
    'bredband2.com',
    'comhem.se',
    'tele2.se',
    'spray.se',
    'passagen.se',
    'live.se',
    'hotmail.se',
    'outlook.se',
    'yahoo.se',
]

const setCurrentTab = (tab) => {
    currentTab.value = tab.name
}

const loadLead = async () => {
    const res = await axios.get(route('api.crm.lead.show', props.leadId))

    lead.value = res.data.lead
}

const updateLead = async () => {
    const res = axios.put(route('api.crm.lead.update', props.leadId), lead.value)
}

const showAll = computed(() => {
    return currentTab.value === 'all';
})

const normalizeDomain = (domain) => {
    if (!domain || typeof domain !== 'string') {
        return null;
    }

    return domain.trim().replace(/^www\./i, '').toLowerCase();
}

const sanitizeWebsiteDomain = (website) => {
    if (!website || typeof website !== 'string') {
        return null;
    }

    let candidate = website.trim();

    if (candidate.length === 0) {
        return null;
    }

    try {
        if (!/^https?:\/\//i.test(candidate)) {
            candidate = `https://${candidate}`;
        }
        const parsed = new URL(candidate);

        if (!parsed.hostname) {
            return null;
        }

        return normalizeDomain(parsed.hostname);
    } catch (error) {
        const manualDomain = candidate.replace(/^https?:\/\//i, '');
        const fallback = manualDomain.split('/')[0];

        return normalizeDomain(fallback);
    }
}

const sanitizeEmailDomain = (email) => {
    if (!email || typeof email !== 'string') {
        return null;
    }

    const [, domain] = email.trim().toLowerCase().split('@');

    if (!domain || domain.length === 0) {
        return null;
    }

    if (GENERIC_EMAIL_DOMAINS.includes(domain)) {
        return null;
    }

    return normalizeDomain(domain);
}

const sanitizedDomain = computed(() => {
    if (!lead.value) {
        return null;
    }

    const fromWebsite = sanitizeWebsiteDomain(lead.value.website);

    if (fromWebsite) {
        return fromWebsite;
    }

    return sanitizeEmailDomain(lead.value.email);
})

const leadLogoUrl = computed(() => {
    if (!sanitizedDomain.value) {
        return null;
    }

    return `https://img.logo.dev/${sanitizedDomain.value}?token=pk_ecY6H6QYQRi7BVpPkCKfdg&size=300&retina=true`;
})

onMounted(() => {
    loadLead()
    currentTab.value = tabs.value[0].name

    if (props.legacyDeals === false) {
        tabs.value.push({
            label: _mft('crm:leadPage.tabTitle.deals'),
            name: 'deals'
        })
    }
})

provide('lead', lead)

</script>
<template>
    <div v-if="lead === null" class="py-8">
        <mf-spinner-medium class="w-24 h-12" />
    </div>
    <div v-else>
        <div class="flex pt-2">
            <div class="w-full">
                <div class="flex items-start mb-3">
                    <img
                        v-if="leadLogoUrl"
                        :src="leadLogoUrl"
                        :alt="`${lead.company ?? lead.name} logo`"
                        class="object-contain bg-white rounded-lg w-14 h-14 ring-1 ring-gray-200 shrink-0"
                    />
                    <div class="ml-3 md:ml-4">
                        <h1 class="font-semibold">{{ lead.name }} - {{ lead.company }}</h1>
                        <p class="text-xxs">
                            <span class="block"><mf-translate domainkey="crm:addedAt"></mf-translate> {{ lead.created_at_tz }}</span>
                            <span v-if="lead.created_at_tz !== lead.updated_at_tz" class="block"><mf-translate domainkey="crm:updatedAt"></mf-translate> {{ lead.updated_at_tz }}</span>
                        </p>
                    </div>
                </div>
            </div>
            <div>
                <button-base
                    style-type="primary"
                    @click="showInsights = ! showInsights"
                >
                    <template #icon>
                        <i class="scale-125 fa-regular fa-head-side-brain"></i>
                    </template>
                    <span class="hidden md:block ml-1.5">Insights</span>
                </button-base>

                <add-insights-domain-with-parser
                    v-if="showInsights"
                    :url="lead.website"
                    :email="lead.email"
                    @close="showInsights = false"
                ></add-insights-domain-with-parser>
            </div>
        </div>

        <tags-manage-inline
            class="mb-8"
            ref="tagsManage"
            :lead-id="lead.id"
        ></tags-manage-inline>

        <ul class="flex-wrap hidden mb-4 space-x-2 xl:space-x-3 md:flex">
            <li
                v-for="(tab, i) in tabs"
                v-bind:key="i"
                v-on:click="setCurrentTab(tab)"
                class="px-1 py-2 mb-4 font-bold border-b-2 cursor-pointer select-none xl:px-3 whitespace-nowrap"
                :class="{'border-myflowMagenta text-myflowMagenta' : currentTab === tab.name }"
            >
                {{ tab.label }}
            </li>
        </ul>

        <card-basic
            v-if="currentTab === 'details' || showAll"
            class="overflow-visible"
        >
            <lead-details
                @update:lead="updateLead"
                @reload:lead="loadLead"
            />
        </card-basic>

        <signatures-list-crm
            v-if="currentTab === 'signatures' || showAll"
            :signatures="signatureRequests"
        ></signatures-list-crm>

        <deliveries-list-crm
            v-if="currentTab === 'deliveries' || showAll"
            :sms-deliveries="smsDeliveries"
            :email-deliveries="emailDeliveries"
        ></deliveries-list-crm>

        <lead-note-card
            v-if="currentTab === 'notes' || showAll"
            :lead="lead"
        ></lead-note-card>

        <lead-action-card
            v-if="currentTab === 'actions' || showAll"
            :lead="lead"
            :actions="actions"
        ></lead-action-card>

        <lead-deal-card
            v-if="legacyDeals === false && (currentTab === 'deals' || showAll)"
            :lead="lead"
            :deals="deals"
            :internal="internal"
        ></lead-deal-card>

        <lead-stakeholder-card
            v-if="currentTab === 'stakeholders' || showAll"
            :lead="lead"
            :stakeholders="stakeholders"
        ></lead-stakeholder-card>
    </div>
</template>
